import type { QueryClient } from '@tanstack/react-query'
import type { NotificationAPI, OpenAPIResponseData } from './api-switcher'

export const comparePhotoKeys = {
  all: ['compare-photo'] as const,
  detail: (photo_id: string) => [...comparePhotoKeys.all, photo_id] as const,
}

export const invalidatePhotoDetailQueries = (queryClient: QueryClient, photo_id: string) =>
  queryClient.invalidateQueries({queryKey: comparePhotoKeys.detail(photo_id)})

export const groupKeys = {
  list: ['groups'] as const,
  suggestionUsers: ['groups', 'suggestion-users'] as const,
  detail: (group_id: string) => ['group', group_id] as const,
}

export const bookUserKeys = {
  list: ['book-users'] as const,
}

export const searchKeys = {
  allUsers: ['search', 'users'] as const,
  allSuggestions: ['search', 'suggestions'] as const,
  owners: ['search', 'owners'] as const,
  inUsers: ['search', 'in-users'] as const,
  worlds: (account_id: string, term: string) => ['search', 'worlds', account_id, term] as const,
  users: (account_id: string, term: string) => ['search', 'users', account_id, term] as const,
  suggestions: (scope: 'book' | 'tag' | 'none', account_id?: string) =>
    account_id === undefined ? ['search', 'suggestions', scope] as const : ['search', 'suggestions', scope, account_id] as const,
}

export const favoriteKeys = {
  list: ['favorites'] as const,
  detail: (favorite_id: string) => ['favorite', favorite_id] as const,
}

export const invalidateFavoriteListQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: favoriteKeys.list})

export const albumKeys = {
  list: ['albums'] as const,
  detail: (album_id: string) => ['album', album_id] as const,
}

export const invalidateAlbumListQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: albumKeys.list})

export const invalidateAlbumDetailQueries = (queryClient: QueryClient, album_id: string) =>
  queryClient.invalidateQueries({queryKey: albumKeys.detail(album_id)})

export const shareKeys = {
  list: ['shares'] as const,
  detail: (share_id: string) => ['share', share_id] as const,
  appendable: (variant?: 'public' | 'public_lock') =>
    variant ? (['shares', 'appendable', variant] as const) : (['shares', 'appendable'] as const),
}

export const invalidateShareListQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: shareKeys.list})

export const invalidateShareDetailQueries = (queryClient: QueryClient, share_id: string) =>
  queryClient.invalidateQueries({queryKey: shareKeys.detail(share_id)})

export const mutedUserKeys = {
  list: ['muted-users'] as const,
}

// ミュート状態の変更はサーバー計算の is_mute を持つ全クエリ（写真・シェア・
// メンバー・ユーザー詳細・検索など）に波及するため、キャッシュ全体を無効化する。
// ミュート操作は稀なのでアクティブビューの再取得コストは許容できる。
export const invalidateMutedUserQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries()

export const dmKeys = {
  list: ['dms'] as const,
  searchPage: ['dm-search-page'] as const,
  searchPageQuery: (search: string) => [...dmKeys.searchPage, search] as const,
}

export const invalidateDMQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({queryKey: dmKeys.list})
  queryClient.invalidateQueries({queryKey: dmKeys.searchPage})
}

export const notificationKeys = {
  list: ['notifications', 'list'] as const,
  invitations: ['notifications', 'invitations'] as const,
  countAll: ['notifications', 'count'] as const,
  count: (user_id: string) => ['notifications', 'count', user_id] as const,
}

export type NotificationsCount = OpenAPIResponseData<ReturnType<typeof NotificationAPI.countNewNotifications>>

export const emptyNotificationsCount: NotificationsCount = {share_album: 0, dm: 0, reaction: 0}

export const trashKeys = {
  all: ['trash'] as const,
  items: ['trash', 'items'] as const,
}

// ゴミ箱を変える書き込みは画面をまたいで多数あるので、入口は api/trash-query.ts の
// invalidateTrashOnSuccess に寄せる。
export const invalidateTrashQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: trashKeys.all})

export const planKeys = {
  list: ['plans'] as const,
  creditCard: ['plans', 'credit-card'] as const,
  storageBreakdown: ['plans', 'storage-breakdown'] as const,
}

export const invalidatePlanStorageBreakdownQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: planKeys.storageBreakdown})

// 通知一覧の notificationKeys(['notifications', ...])とは別物。root を分けて prefix 衝突を避ける。
export const settingKeys = {
  notification: ['setting', 'notification'] as const,
}

export const invalidateNotificationSettingQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: settingKeys.notification})

export const meKeys = {
  me: ['me'] as const,
}

export const historyKeys = {
  all: ['history'] as const,
  account: (account_id: string) => ['history', account_id] as const,
  userIndex: (account_id: string) => ['history', account_id, 'user', 'index'] as const,
  userScope: (account_id: string, scope_key: string) => ['history', account_id, 'user', scope_key] as const,
  userRange: (account_id: string, scope_key: string, generation: string, desc: boolean, offset_key: string, offset_id: string, user_id?: string) =>
    [...historyKeys.userScope(account_id, scope_key), 'range', generation, desc, offset_key, offset_id, user_id ?? null] as const,
  userDetail: (account_id: string, scope_key: string, user_id: string) =>
    [...historyKeys.userScope(account_id, scope_key), 'detail', user_id] as const,
  userPage: (account_id: string, scope_key: string, user_id: string, desc: boolean, offset_key?: string | null) =>
    [...historyKeys.userDetail(account_id, scope_key, user_id), 'page', desc, offset_key ?? null] as const,
  userBoundaries: (account_id: string, scope_key: string, user_id: string, owner_key: string, desc: boolean) =>
    [...historyKeys.userDetail(account_id, scope_key, user_id), 'boundaries', owner_key, desc] as const,
  userEncounter: (account_id: string, scope_key: string, user_id: string, started_at_key: string) =>
    [...historyKeys.userDetail(account_id, scope_key, user_id), 'encounter', started_at_key] as const,
  index: (account_id: string) => ['history', account_id, 'world', 'index'] as const,
  scope: (account_id: string, scope_key: string) => ['history', account_id, 'world', scope_key] as const,
  filteredIndex: (account_id: string, scope_key: string, world_id: string) =>
    [...historyKeys.scope(account_id, scope_key), 'filtered-index', world_id] as const,
  range: (account_id: string, scope_key: string, generation: string, desc: boolean, offset_key: string, world_id?: string) =>
    [...historyKeys.scope(account_id, scope_key), 'range', generation, desc, offset_key, world_id ?? null] as const,
  detail: (account_id: string, scope_key: string, joined_at_key: string) =>
    [...historyKeys.scope(account_id, scope_key), 'detail', joined_at_key] as const,
  encounter: (account_id: string, scope_key: string, vrchat_user_id: string) =>
    [...historyKeys.scope(account_id, scope_key), 'encounter', vrchat_user_id] as const,
  photo: (account_id: string, scope_key: string, photo_id: string) =>
    [...historyKeys.scope(account_id, scope_key), 'photo', photo_id] as const,
}

export const invalidateHistoryQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: historyKeys.all})

export const invalidateMeQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: meKeys.me})

// Cognito に登録したパスキー（WebAuthn 資格情報）。Amplify から直接読むので api-switcher を通らない。
// 読み直しなしで別のアカウントに入り直しても前の人の一覧を見せないよう、アカウントごとにキーを分ける
export const passkeyKeys = {
  all: ['passkeys'] as const,
  list: (account_id: string) => ['passkeys', account_id] as const,
}

export const invalidatePasskeyQueries = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({queryKey: passkeyKeys.all})
